import { INavPageClientInfo, IPageProperty, IPageQueryConstraint, NavClientService, NavItemAnchor, NavLineData, NavPageItem, NavPageItems, NavPropertyItem, Observable, ResolveNavUrl, TDefinedPageName, TNavUrlAccess, INavKey } from '@common/navdata';
import { IRWF3DocLink } from '@common/preview/preview.shared';
import * as rwf3 from '@common/rwf';
import {
  AccountingMode, CloseWorkflowInstance, OpenBreakoutInNav, GetKWF3DocumentUrl, IFDRW_AdresseKarte,
  IFDRW_WFInstance, LoadPageItemFromWizardItem, LoadWfInstance, LogReauthenticate, OpenWFInstance, ReAuthenticate, RouteWorkflowInstance, RwfInvoiceDataService, SaveRoutingInfo,
  TWFInstanceID, ValidateSignature,
  FDRW_Anordnungsblatt as Page_FDRW_Anordnungsblatt,
  FDRW_Auftragsbuchblatt as Page_FDRW_Auftragsbuchblatt
} from '@common/rwf';
import { IKWFComment, IKWFRoutingAction, IKWFRoutingInfo, LoadActions, ResolveInitialAction } from '@common/rwf/routeinstancemodel';
import { FDDI_DMS_Purchase_Document, FDDI_DMS_Sales_Document, FDDI_Wizard_Create_New_DMS_Document, FDRW_Anordnungsblatt, FDRW_Auftrag, FDRW_Auftragsbuchblatt, FDRW_Wizard, METHOD } from '@common/rwf/rwf.const';
import { fn, INamed, IInfOperation } from '@common/utils';
import { AsObservableArray, Bind, WrapAsObservable, SetRWFCustomColor } from '@common/navkendo';
import { DisplayPageItem, DisplayPage_Main, DisplayPage_Wizard, IKWFSessionState } from '@common/rwf/rwf.edit.d';

import { CompatibilityService } from '@services/compatibilityservice';
import { IDialogService } from '@services/dialogservice';
import { KWFFormSession } from '../KWFEdit';
import { ErrToMsg, PROP_Document_Type, PROP_LuGMAddRec, ServiceEvents, SessionEvents as _Events } from '@common/rwf/rwfedit.common';
import UISafe = fn.UISafe;
import resolveToError = fn.resolveToError;
import checkPromise = fn.checkPromise;
import rwf3Pages = rwf3.Pages;
import { ShowModalReadMultipleSelect } from '../navkendo';
import { IEvent, IPageObj } from '@common/nav';

/** Base Adapter Functionality for RWF3 Kameral / Doppisch */
export class RWF3BaseService extends Observable {

  /** Create doppischen or kameralen ServiceAdapter */
  public static Create($c: CompatibilityService, idOrNo: TWFInstanceID, navUrl: TNavUrlAccess) {
    const $rwf = $c.resolve("Rwf")("RwfBootstrap");
    return LoadWfInstance($c, idOrNo, navUrl).then(wfInstance => {
      return $rwf.Resolve.then(rwf => {
        return new RWF3BaseService($c, wfInstance, rwf.RwfSetup, rwf.RwfUser);
      });
    });
  }

  readonly IsKameral: boolean;

  readonly IsStandalone: boolean;

  /** only set in kameral scenario */
  public readonly HuelState?: IKamHuelState & kendo.data.ObservableObject;

  public readonly AdressInfo?: IKamAdressInfo;
  public readonly WizardPagename: DisplayPage_Wizard;

  public CurrentWizardOnlyFields: string[] = [];
  constructor(
    protected readonly $c: CompatibilityService,
    public WFInstance: NavPageItem<rwf3.IFDRW_WFInstance>,
    public readonly WFSettings: Readonly<rwf3.IFDRW_Workflow_Setup_WS>,
    public readonly WFUser: Readonly<rwf3.IFDRW_WF_User>) {
    super();
    this.IsStandalone = $c.resolve("Rwf")("RwfBootstrap").EnsureResolved().RwfSettings.User.IsStandalone == true;

    this.IsKameral = WFInstance.GetValue("AccountingMode") === AccountingMode.Kameral;
    this.WizardPagename = this.IsKameral ? FDRW_Wizard : FDDI_Wizard_Create_New_DMS_Document;
    this.NavClient = $c.resolve("NavClient");
    this.DialogService = $c.resolve("DialogService");
    var urlInfo = ResolveNavUrl(WFInstance.ClientInfo.Url);
    this.navUrl = urlInfo.FullUrl;
    this.company = urlInfo.Mandant;
    // Ensure only one Call for Used Page Infos
    const toLoadPageNames: TDefinedPageName[] = [
      "FDRW_WF_Action_Fwd_Comments_List",
      "FDRW_Workflow_Field_Used",
      "FDRW_Workflow_Step_List",
      "FDRW_Workflow_Instance_Action_List",
      <TDefinedPageName>WFInstance.PageName
    ];

    if (this.IsKameral) {

      const huel = this.HuelState = WrapAsObservable({
        Show: false,
        Title: "Huel Aufteilung",
        /** Resolve HuelClient Info */
        HuelClient: <INavPageClientInfo>undefined,
        /** Current State for Mandatory/Readonly Fields on Huel Items */
        HuelFields: <IFormFieldInfo>undefined,
        Current: <NavPageItems>undefined,
      });

      this.bind(ServiceEvents.ApplyMandatoryFields, (e: { HuelInfo: IFormFieldInfo }) => {
        var fieldInfo = huel.HuelFields = e && e.HuelInfo,
          currHuel = huel.Current && huel.Current.Anchor;
        if (currHuel) {
          currHuel.Mandatory.ReplaceValues("HUEL", (fieldInfo && fieldInfo.Mandatory) || []);
          currHuel.Readonly.ReplaceValues("HUEL", (fieldInfo && fieldInfo.Locked) || []);
        }
      });
    }

    this.AdressInfo = {
      /** Current Adress Info Page Item */
      Item: <NavPageItem>undefined,
      /** Current AddressNo */
      AddressNo: "",
      /** Current State of AdressInfo */
      Loading: false,
      /** Error Info on failure */
      Error: "",
    };
  }

  protected readonly navUrl: string;
  protected readonly company: string;
  public readonly NavClient: NavClientService;
  /** FWF Specific : do not display Wizard etc. ... */
  public SourceRecordInfos?: { Title: string, Value: string }[];
  public SourceRecordNavigate?: IInfOperation & { Hint: string };
  private readonly DialogService: IDialogService;

  /** allows to defer initalization */
  protected initSession(session: KWFFormSession): any | PromiseLike<any> {
    let refreshInfo = fn.proxy(this.AddInfo.Refresh, this, false);
    session.bind(_Events.displaychange, () => {
      var item = session.DisplayItem;
      if (item) {
        session.one(_Events.displaychange, item.RegisterPropertyChanged("*", refreshInfo));
        // Refresh On Current Main Line Item Change also
        if (session.DisplayMainLineData)
          session.one(_Events.displaychange, Bind(session, refreshInfo, _Events.MainLineSelectionChanged));

        // Refresh Info on "ReserveFunds"
        if (this.WFInstance)
          session.one(_Events.displaychange, this.WFInstance.RegisterMethodExecuted(() => this.AddInfo.Refresh(true),
            WFInstance.Methods.ReserveFunds.MethodName));
      }

      refreshInfo();
    });

    if (this.IsKameral) {
      this.initSessionKameral(session);
    } else {
      this.initSessionDoppik(session);
    }
  }

  /** Init kameral specific session config */
  private initSessionKameral(session: KWFFormSession) {
    let HuelState = this.HuelState;
    let info = this.AdressInfo;
    let $c = this.$c;
    let NavClient = this.NavClient;
    let navUrl = this.navUrl;

    var resolveHuel = NavClient.GetPageClientInfo<"FDRW_HuelAufteilung">("FDRW_HuelAufteilung", navUrl, false).then((client) => {
      HuelState.HuelClient = client;
    }).then(fn.noop, err => {
      session.AddNotification({ message: resolveToError(err), messageType: "error" });
    });

    return $c.all([resolveHuel]).then(() => {
      // for these Commands the Huel Aufteilung must be validated
      session.bindWithDefer(() => validateHuelAufteilung(this), "Route", "Sign");

      // SetUp AdressInfo State Changes
      const adressNo = "Adressnr", //+ "Firma",
        einnahmeAusgabe = "EinnahmeAusgabe",
        einAus = "Ein_Aus",
        digitalInvoiceMessage = "Für diese Adresse ist der digitale Rechnungsversand aktiviert.",
        digitalInvoiceGroup = "rwf.address.digitalInvoice";

      let clearDigitalInvoiceNotification = fn.noop;

      const
        resolveKameralDocTypeField = () => {
          const item = session.DisplayItem;
          if (!item)
            return "";
          if (item.PageName == FDRW_Wizard)
            return einnahmeAusgabe;
          if (item.PageName == FDRW_Anordnungsblatt)
            return einAus;
          return "";
        },
        evaluateDigitalInvoiceNotification = () => {
          clearDigitalInvoiceNotification();
          const addressItem = info.Item;
          const model = <any>(addressItem && addressItem.Model);
          const displayItem = session.DisplayItem;
          const docTypeField = resolveKameralDocTypeField();
          const docTypeValue = displayItem && docTypeField && displayItem.GetValue(docTypeField, true, true);
          const docTypeText = displayItem && docTypeField && displayItem.GetValueEx(docTypeField);
          const isAllowedDocType = docTypeValue === 0 || docTypeValue === 2
            || docTypeText === "Einnahme_SK" || docTypeText === "Einnahme_PK"
            || docTypeText === "Einnahme";
          const hasIvmPdf = !!(model && model.IAX_IVM_Send_PDF);
          const hasEInvoice = !!(model && model.IAX_FERO_AllowEInvoice);

          if (isAllowedDocType && (hasIvmPdf || hasEInvoice)) {
            clearDigitalInvoiceNotification = session.AddNotification({
              message: digitalInvoiceMessage, messageType: "info", group: digitalInvoiceGroup
            });
          }
        },
        adressNoChanged = () => {
          const displayItem = session.DisplayItem;
          const id: string = displayItem && displayItem.GetValue(adressNo, true, true);
          const wfInstanceId = displayItem && displayItem.Anchor.InstanceID;

          if (info.AddressNo !== id) {
            info.AddressNo = id;
            delete info.Error;
            clearDigitalInvoiceNotification();
            if (!id)
              info.Item = undefined;
            else
              info.Loading = true;

            const resetLoading = () => {
              if (id === info.AddressNo) {
                info.Loading = false;
              }
            };
            $c.when(id && NavClient.LoadItem<rwf3.IFDRW_AdresseKarte>({
              PageName: "FDRW_AdresseKarte", NavUrl: navUrl,
              Keys: { Nr: id }, InstanceID: wfInstanceId
            })).then(item => {
              if (item) {
                item.Anchor.Readonly.SetForceAll(true);
              }
              if (id === info.AddressNo) {
                info.Item = item;
              }
              if (item.Model.Insolvenz) {
                session.AddNotification({
                  message: "Information - bei dieser Adresse ist der Haken für Insolvenz gesetzt!", messageType: "info"
                });
              }
              evaluateDigitalInvoiceNotification();
            }, err => {
              if (id == info.AddressNo)
                info.Error = resolveToError(err);
            }).then(resetLoading, resetLoading);
          }
        };
      session.bind(_Events.displaychange, () => {
        var item = session.DisplayItem;
        if (item) {
          if (item.ClientInfo && item.ClientInfo.AllProperties && item.ClientInfo.AllProperties[adressNo]) {
            session.one(_Events.displaychange, item.RegisterPropertyChanged(item.ResolveProperty(adressNo, true, true), adressNoChanged));
          }
          const docTypeField = resolveKameralDocTypeField();
          if (docTypeField && item.ClientInfo && item.ClientInfo.AllProperties && item.ClientInfo.AllProperties[docTypeField]) {
            session.one(_Events.displaychange, item.RegisterPropertyChanged(item.ResolveProperty(docTypeField, true, true), evaluateDigitalInvoiceNotification));
          }
        }
        adressNoChanged();
      });

      session.bind(_Events.displaychange, () => HuelState.Show = this.WFSettings.IsHUELAufteilungActive && [FDRW_Anordnungsblatt, FDRW_Auftragsbuchblatt].indexOf(session.DisplayPageName) >= 0);
      session.bind(_Events.destroyed, clearDigitalInvoiceNotification);
    });
  }

  /** Init doppik specific session config */
  private initSessionDoppik(session: KWFFormSession) {
    let info = this.AdressInfo;
    let $c = this.$c;
    let NavClient = this.NavClient;
    let navUrl = this.navUrl;

    const adressNo = "Adressnummer",
      wizardAdressNo = "No",
      accountingProp = "Accounting",
      documentTypeProp = "Document_Type",
      digitalInvoiceMessage = "Für diese Adresse ist der digitale Rechnungsversand aktiviert.",
      digitalInvoiceGroup = "rwf.address.digitalInvoice";

    let clearDigitalInvoiceNotification = fn.noop;

    const isAllowedDoppikDocType = () => {
      const item = session.DisplayItem;
      if (!item)
        return false;

      // Handle Wizard page: check Accounting and Document_Type
      if (item.PageName === FDDI_Wizard_Create_New_DMS_Document) {
        const accountingValue = item.GetValue(accountingProp, true, true);
        const accountingText = item.GetValueEx(accountingProp);
        const isVerkauf = accountingValue === 0 || accountingText === "Customer";

        const documentTypeValue = item.GetValue(documentTypeProp, true, true);
        const documentTypeText = item.GetValueEx(documentTypeProp);
        const isAllowedDocumentType = documentTypeValue === 3 || documentTypeValue === 4
          || documentTypeText === "Invoice" || documentTypeText === "Credit_Memo";

        return isVerkauf && isAllowedDocumentType;
      }

      // Handle Sales Document page: only Invoice and Credit_Memo are available, so always show notification
      if (item.PageName === FDDI_DMS_Sales_Document) {
        return true;
      }

      return false;
    };

    const evaluateDigitalInvoiceNotification = () => {
      clearDigitalInvoiceNotification();
      const addressItem = info.Item;
      const model = <any>(addressItem && addressItem.Model);
      const hasIvmPdf = !!(model && model.IAX_IVM_Send_PDF);
      const hasEInvoice = !!(model && model.IAX_FERO_AllowEInvoice);

      if (isAllowedDoppikDocType() && (hasIvmPdf || hasEInvoice)) {
        clearDigitalInvoiceNotification = session.AddNotification({
          message: digitalInvoiceMessage, messageType: "info", group: digitalInvoiceGroup
        });
      }
    };

    const adressNoChanged = () => {
      const displayItem = session.DisplayItem;
      if (!displayItem) {
        clearDigitalInvoiceNotification();
        info.AddressNo = "";
        info.Item = undefined;
        return;
      }

      // Determine which page and corresponding address field
      let addressField: string = "";
      if (displayItem.PageName === FDDI_Wizard_Create_New_DMS_Document) {
        addressField = wizardAdressNo;
      } else if (displayItem.PageName === FDDI_DMS_Sales_Document) {
        addressField = "Sell_to_Customer_No";
      } else {
        clearDigitalInvoiceNotification();
        info.AddressNo = "";
        info.Item = undefined;
        return;
      }

      const id: string = addressField && displayItem.GetValue(addressField, true, true);
      const wfInstanceId = displayItem.Anchor.InstanceID;

      if (info.AddressNo !== id) {
        info.AddressNo = id;
        delete info.Error;
        clearDigitalInvoiceNotification();
        if (!id)
          info.Item = undefined;
        else
          info.Loading = true;

        const resetLoading = () => {
          if (id === info.AddressNo) {
            info.Loading = false;
          }
        };
        $c.when(id && NavClient.LoadItem<rwf3.IFDRW_AdresseKarte>({
          PageName: "FDRW_AdresseKarte", NavUrl: navUrl,
          Keys: { Nr: id }, InstanceID: wfInstanceId
        })).then(item => {
          if (item) {
            item.Anchor.Readonly.SetForceAll(true);
          }
          if (id === info.AddressNo) {
            info.Item = item;
          }
          evaluateDigitalInvoiceNotification();
        }, err => {
          if (id == info.AddressNo)
            info.Error = resolveToError(err);
        }).then(resetLoading, resetLoading);
      }
    };

    session.bind(_Events.displaychange, () => {
      var item = session.DisplayItem;

      // Wizard page bindings
      if (item && item.PageName == FDDI_Wizard_Create_New_DMS_Document) {
        if (item.ClientInfo && item.ClientInfo.AllProperties && item.ClientInfo.AllProperties[wizardAdressNo]) {
          session.one(_Events.displaychange, item.RegisterPropertyChanged(item.ResolveProperty(wizardAdressNo, true, true), adressNoChanged));
        }
        if (item.ClientInfo && item.ClientInfo.AllProperties && item.ClientInfo.AllProperties[documentTypeProp]) {
          session.one(_Events.displaychange, item.RegisterPropertyChanged(item.ResolveProperty(documentTypeProp, true, true), evaluateDigitalInvoiceNotification));
        }
        if (item.ClientInfo && item.ClientInfo.AllProperties && item.ClientInfo.AllProperties[accountingProp]) {
          session.one(_Events.displaychange, item.RegisterPropertyChanged(item.ResolveProperty(accountingProp, true, true), evaluateDigitalInvoiceNotification));
        }
      }

      // Sales document page bindings
      if (item && item.PageName == FDDI_DMS_Sales_Document) {
        if (item.ClientInfo && item.ClientInfo.AllProperties && item.ClientInfo.AllProperties["Sell_to_Customer_No"]) {
          session.one(_Events.displaychange, item.RegisterPropertyChanged(item.ResolveProperty("Sell_to_Customer_No", true, true), adressNoChanged));
        }
      }

      adressNoChanged();
    });

    session.bind(_Events.destroyed, clearDigitalInvoiceNotification);
  }

  public readonly AddInfo = {
    Show: false,
    Title: "...",
    Info: "",
    Loading: false,
    Curr: { Promise: <PromiseLike<any>>undefined, Key: "-" },
    Refresh: (force: boolean) => {
      let info = this.AddInfo,
        p = info.CurrParams();
      if (!force && p && info.Curr && info.Curr.Key == p.Key)
        return;

      var anchor = this.Session.DisplayItem && this.Session.DisplayItem.Anchor,
        waitFor = this.$c.when(anchor && anchor.AfterOperations());
      info.Loading = true;
      const startAction = () => {
        if (info.Curr !== start)
          return;
        return info.LoadInfo().then(l => {
          if (info.Curr == start) {
            info.Loading = false;
            if (info.Show = !!l) {
              info.Title = l.Title;
              info.Info = l.Info;
            }
          }
        });
      };
      var start = info.Curr = {
        Promise: waitFor.then(startAction, startAction),
        Key: p && p.Key
      };
    },

    LoadInfo: () => {
      var session = this.Session,
        info = this.AddInfo,
        result = info.CurrParams();
      if (info.Show = !!result) {
        info.Title = result.Title;
      }

      if (this.IsStandalone && result && result.PKey) {
        const instanceId = session.DisplayItem.Anchor.InstanceID;
        fn.ExtendObject<any>(result.PKey, { WorkflowInstanceID: instanceId });
        var full = session.Anchor.Service.Read<IFDRW_Workflow_Budget_Info>({
          NavUrl: session.DisplayItem.ClientInfo.Url,
          Keys: <any>result.PKey,
          PageName: "FDRW_Workflow_Budget_Info", // Is "Standalone only" ...
          InstanceID: instanceId
        })
          .then(item => {
            // Normalize to "non Setra" behavior
            result.Info = ((item && item.Data && item.Data.Info) || "Keine Informationen verfügbar").split("\\").join("\n");
          }, err => result.Info = resolveToError(err));
      } else {
        var full = this.$c.when(result && session.DisplayItem.ExecuteMethod({ MethodName: result.Method, RowKey: result.RowKey, RowProperty: result.RowProperty }, null, true).then(r => {
          result.Info = ErrToMsg((r && <string>r["Result"]) || "");
        }, err => result.Info = resolveToError(err)));
      }

      return full.then(fn.ToFunc(result));
    },

    CurrParams: () => {
      const session = this.Session,
        methodGetBudgetInfo = METHOD.GetBudgetInfo,
        item = session.DisplayItem;
      var getKey = (f: string[], exItem: NavPropertyItem) => f.map(n => (exItem && exItem.GetValue(n)) || "").join("|"),
        result: { PKey: { [name in keyof IFDRW_Workflow_Budget_Info]?: any }, Title: string, Info?: string, Key: string, Method: string, RowKey?: string, RowProperty?: string };
      switch (session.DisplayPageName) {
        case FDRW_Auftragsbuchblatt:
          const aBuBla = item as Page_FDRW_Auftragsbuchblatt;
          result = {
            Title: "Auftragsstatistik", Method: "GetOrderInfo", Key: getKey(["Auftragsnr", "Auftragsposition_Nr"], item),
            PKey: {
              DocumentNo: aBuBla.GetValue("Auftragsnr", true),
              LineNo: aBuBla.GetValue("Auftragsposition_Nr", true),
            }
          };
          break;
        case FDRW_Anordnungsblatt:
          const aoBlatt = item as Page_FDRW_Anordnungsblatt;
          result = {
            Title: "Budgetinformationen", Method: methodGetBudgetInfo, Key: item.Key,
            PKey: {
              DocumentNo: "", // Convention
              LineNo: aoBlatt.GetValue("Zeilennr"),
            }
          };
          break;
        case FDDI_DMS_Purchase_Document:
          var canDetermine = (session && session.KWFService && session.KWFService.IsStandalone) || (item && item.ClientInfo.AllMethods[methodGetBudgetInfo]),
            lData = <NavLineData>session.DisplayMainLineData,
            selRow = lData && (lData.SelectedItems[0] || lData.Items[0]),
            selRowId = selRow && selRow.Key;
          result = lData && selRowId && canDetermine && {
            RowProperty: lData && lData.MetaData.PropertyName, RowKey: selRowId, Title: "Budgetinformationen", Method: methodGetBudgetInfo, Key: selRow && (selRow.Key + "-" + item.Key),
            PKey: {
              DocumentNo: selRow.GetValue("Document_No"),
              LineNo: selRow.GetValue("Line_No"),
            }
          };
          break;
        case FDRW_Auftrag:
          // Auftrag : Info from Current Main Line!
          var lData = <NavLineData>session.DisplayMainLineData,
            selRow = lData && lData.SelectedItems[0],
            selRowId = selRow && selRow.Key;

          result = selRowId && {
            Title: "Budgetinformationen", Method: methodGetBudgetInfo, RowKey: selRowId,
            RowProperty: lData.MetaData.PropertyName,
            Key: getKey(["Haushalt", "Gliederung", "Gruppierung", "Betrag"], selRow),
            PKey: {
              DocumentNo: selRow.GetValue("Auftragsnr"),
              LineNo: selRow.GetValue("Zeilennr"),
            }
          };
          break;
        default:
        // ??
      }

      return result;
    }
  };

  /** Load Wizard Item by Guid and (optional) url */
  protected LoadWizardItem(guid: string) {
    return this.NavClient.LoadItem<rwf3.IFDDI_Wizard_Create_New_DMS_Document | rwf3.IFDRW_Wizard>({
      PageName: this.WizardPagename,
      NavUrl: this.navUrl,
      Keys: { GUID: guid },
      InstanceID: this.WFInstance.Anchor.InstanceID
    }).then(w => {
      if (!w) {
        fn.error("Vorkontierung mit ID '" + guid + "' nicht gefunden.");
      }

      return w;
    });
  }

  /** Load Page Item from given Wizard Item (or non if not yet "Weiter zur Kontierung") */
  protected LoadPageItem(wizardItem: NavPageItem): PromiseLike<NavPageItem | NavPageItems> {
    // TODO: was ist mit "Rechnung darf nicht aus der Vorkontierung weitergeleitet werden ?"
    const $c = this.$c;
    return LoadPageItemFromWizardItem($c, wizardItem, this.IsStandalone);
  }

  /** Reload DisplayItems/DisplayView for given FormSession and Transfer Information to Session */
  protected reloadDisplayItems(session: KWFFormSession, _wizard?: PromiseLike<NavPageItem>): PromiseLike<KWFFormSession> {
    var navClient = this.NavClient,
      $c = this.$c,
      viewName = '';
    // vgl. Diskussion in WI-168123
    // viewName = this.IsStandalone ? 'RwfOnlyUser' : '';
    _wizard = _wizard || this.LoadWizardItem(session.WizardGuid);
    return _wizard.then(wizardItem => {
      return this.LoadPageItem(wizardItem).then(pageItem => {
        var displayItems = pageItem || wizardItem;
        return navClient.GetView(displayItems.ClientInfo, viewName).then((view) => {
          this.WizardItem = wizardItem;
          const wfModel = this.WFInstance && this.WFInstance.Model;
          const SourceRecordInfos = wfModel && wfModel.SourceRecord
            && this.NavClient.ReadMultiple({
              PageName: <any>"FDRW_SourceRecordInfo",
              Constraints: [
                { FieldName: "Name", Value: "*|WorkflowInstanceID" },
                { FieldName: "Value", Value: "*|" + wfModel.ID }
              ],
              PageCount: 0,
              InstanceID: wfModel.ID
            }).then(s => {
              if (s.Data.length == 0) {
                throw new Error(this.IsStandalone ?
                  "Die replizierten Beleginformationen für einfache Workflowbenutzer konnten nicht bestimmt werden!"
                  : "Keine Detailinformationen vorhanden");
              }
              return s.Data.map(item => ({ Title: <string>item.Name, Value: <string>item.Value }));
            }).then(result => {
              return this.SourceRecordInfos = result;
            }, err => {
              return [{ Title: "Fehler", Value: <string>fn.resolveToError(err) }];
            }).then(result => {
              this.SourceRecordInfos = result;
            });

          if (SourceRecordInfos) {
            // In Case of Available SourceRecordDetails: --> Do Not Display Wizard
            // FWF : Reset View to Empty dynamic Groups
            displayItems = null; // <> undefined !!
            view = view && { PageName: "", Groups: [], ViewName: view.ViewName };
          } else {
            this.SourceRecordInfos = undefined;
          }
          this.SourceRecordNavigate = SourceRecordInfos && !this.IsStandalone && {
            Hint: 'Datensatz in newsystem anzeigen',
            Icon: { "k-icon": true, "k-i-hyperlink-open": true },
            Execute: () => rwf3.OpenInNav($c, wfModel.SourceRecord)
          };
          return $c.when(SourceRecordInfos).then(() => session.SetCurrentDisplayItems(displayItems, view).then(() => {
            session.DisplayMandant = this.company || ResolveNavUrl(displayItems.ClientInfo.Url).Mandant;
            SetRWFCustomColor(view.CustomBGColor);
            return session;
          }));
        });
      });
    });
  }

  private kwfStepItem: rwf3.IFDRW_Workflow_Step_List;
  /** Current Workflow Step Info */
  public get CurrWFStep() { return this.kwfStepItem; }
  private kwfActions: IKWFRoutingAction[];
  /** PreviousCheckoutByUser (before open operation) */
  public PreviousCheckoutUser: string;

  /** Marker : Is Instance Checked Out (if not: readonly) */
  public IsCheckedOut: boolean;
  /** Current UI-Bound Key (to be aware of external Changes) */
  private WFInstanceKey: string;
  public WizardItem: NavPageItem;

  public get MainDocLink(): IRWF3DocLink {
    var wfInstanceId = this.WFInstance.GetValue("ID"),
      docs = (<rwf3.IFDRW_WFInstance><any>this.WFInstance.Model).Workflow_Documents_Sub || [],
      mainDoc = docs.filter(item => item.Type == rwf3.Type_10.Main_Document)[0];
    return mainDoc && {
      Url: GetKWF3DocumentUrl(wfInstanceId, undefined, this.navUrl),
      Title: "Hauptdokument",
      NavUrl: this.navUrl,
      WorkflowInstanceID: wfInstanceId,
      ID: (mainDoc && mainDoc.ID) || undefined,
      AllowEdit: this.IsCheckedOut,
      IsArchived: false,
      Extension: "pdf"
    };
  }

  public Session: KWFFormSession;

  /** Attach a fully initialized Edit Session */
  AttachSession(session: KWFFormSession, forceOpen = true): PromiseLike<KWFFormSession> {
    if (this.Session)
      throw new Error("Session already Created!");

    if (session.KWFService != this) {
      throw new Error('Invalid Session object');
    }
    SetRWFCustomColor(undefined);
    var result = this.Session = session; //new KWFFormSession(this.$c, this);
    result.bindWithDefer((e: { DisplayItem: DisplayPageItem }) => {
      var action = result.ValidatedRoutingAction(),
        actionCode = action && action.WFActionCode;

      return e.DisplayItem && this.applyCurrentFieldInfo(e.DisplayItem, actionCode);
    }, _Events.applycurrentfieldinfo);
    var me = this;
    fn.validate(!!this.WFInstance, "KWF3Service : no WfInstace given");
    var startUp = this.$c.when(this.initSession(result)).then(() => this.openWFInstance(this.WFInstance, forceOpen)).then(() => {
      const wf = this.WFInstance.Model;
      result.WizardGuid = wf.ID;
      const wfInstanceAnchor = (<NavItemAnchor<IFDRW_WFInstance>>this.WFInstance.Anchor);
      result.bind(_Events.destroyed,
        wfInstanceAnchor.RegisterPropertyChanged("ReadOnlyView", () => result.RefreshState(me.getState())));
      var startUpResult = this.reloadDisplayItems(result).then(() => this.initBreakout(result).then(breakout => result["Breakout"] = breakout).then(fn.ToFunc(result)));

      // present NotificationText to user if any
      startUpResult.then(session => {
        var notifcationText = session?.KWFService?.WFInstance?.Model.NotificationText;
                if(notifcationText) {
          session.AddNotification({
                        message : notifcationText.replace(/\\/g, '\r\n'),
                        messageType : 'warning'
          });
        }
      });

      // Check for initial Action : Either from Item or Default
      var selectedAction = ResolveInitialAction(wf, this.kwfActions, this.kwfStepItem.DefaultAction);

      if (selectedAction) {
        startUpResult.then(item => result && !result.IsDestroyed && result.RefreshRoutingAction(<any>selectedAction).then(() => result));
      }

      // Check for Selected Forwarding Comment, Remark
      result.CurrentRemark = wf.Remark;
      var selComment = wf.FWCommentCode;
      if (selComment && selectedAction) {
        return this.loadActionComments(selectedAction.WFActionCode).then((cmts) => {
          var match = <any>cmts.filter(item => item.WorkflowForwardingComment == selComment)[0];
          var cmt = <any>match;
          if (cmt) {
            cmt.ID = match.WorkflowForwardingComment;
            cmt.Title = match.Comment;
            result.SelectedForwardingComment = cmt;
          }

          return startUpResult;
        });
      } else
        return startUpResult;
    });

    this.$c.finally(startUp.then(undefined, err => {
      if (this.WFInstance && this.PreviousCheckoutUser != undefined && this.WFInstance.GetValue("CheckedOutBy"))
        this.CloseSession(result, false);
    }), () => {
      if (!result.IsDestroyed) {
        result.bind(_Events.destroyed, () => {
          this.kwfStepItem = undefined;
        });
      }
    });

    return startUp;
  }

  /** Bearbeitung beenden, ohne weiterleiten (Speichern, Schliessen, für alle freigeben) */
  CloseSession(session: KWFFormSession, isZwischenSpeichern: boolean): PromiseLike<void> {
    if (this.closeOperation)
      return this.closeOperation;

    if (this.WFInstance) {
      if (!this.IsCheckedOut) {
        return this.$c.when();
      }
      this.closeOperation = CloseWorkflowInstance(this.WFInstance, {
        IsZwischenSpeichern: isZwischenSpeichern,
        Action: session.ValidatedRoutingAction(),
        Comment: <any>session.SelectedForwardingComment,
        Remark: session.CurrentRemark || ""
      }).then(() => {
        this.WFInstance = null;
        this.IsCheckedOut = undefined;
        SetRWFCustomColor(undefined);
        delete this.WFInstanceKey;
      });
      this.$c.finally<void>(this.closeOperation, () => this.closeOperation = null);
      return this.closeOperation;
    }

    return this.$c.reject("Die Bearbeitung wurde bereits geschlossen!");
  }
  private closeOperation: PromiseLike<void>;

  /** Weiterleiten des Formulars aufgrund aktueller Configuration, implizites "Schliessen" */
  RouteSession(session: KWFFormSession): PromiseLike<any> {

    const selAction = session.ValidatedRoutingAction(),
      instance = this.WFInstance,
      $c = this.$c;

    if (!selAction)
      return $c.reject("Keine Aktion für Weiterleiten gewählt");

    var optSign = $c.when();
    if (selAction.SignatureMandatory == true) {
      optSign = ValidateSignature($c, [{
        Instance: instance,
        CurrentStep: this.kwfStepItem,
        SelectedAction: selAction,
        ID: instance.GetValue("ID", true),
        Name: instance.GetValue(<any>"Name") || instance.GetValue("ID", true)
      }], this.navUrl);
    }

    var result = optSign.then(() => RouteWorkflowInstance(instance, this.CurrentRoutingInfo));
    // In Case of Error : Force Reload of Display Item (Key might be invalid)
    var currItem = session.DisplayItem;
    result.then(fn.noop, err => UISafe(currItem.RevertItem(true, true)));

    return result;
  }

  public get CurrentRoutingInfo() {
    const session = this.Session;
    const selAction = session && session.ValidatedRoutingAction();
    if (!selAction) {
      return;
    }
    return {
      Action: selAction,
      Comment: <any>session.SelectedForwardingComment,
      Remark: session.CurrentRemark || ""
    };
  }

  public LeaveWizard(session: KWFFormSession) {
    return this.executeChangeItemMethod(session, METHOD.InsertNewDoc, "CreateDocument");
  }

  public RevertToWizard(session: KWFFormSession) {
    return this.executeChangeItemMethod(session, METHOD.RemoveCreation, "RevertToWizard");
  }

  public ReAuthenticate() {
    const WFInstance = this.WFInstance;
    const routeInfo = this.CurrentRoutingInfo;

    // for "log reauthenticate" the current Routing Info must be persistet
    return this.$c.when(routeInfo && SaveRoutingInfo(WFInstance, routeInfo)).then(() =>
      ReAuthenticate(this.$c).then(() =>
        LogReauthenticate(WFInstance).then(fn.noop))
    );
  }

  /** Revert to current Server State */
  public RevertDisplay() {
    var WFInstance = this.WFInstance,
      instanceKey = WFInstance.Key,
      currStep = WFInstance.GetValue("CurrentWorkflowStep"),
      currCheckedOutBy = WFInstance.GetValue("CheckedOutBy"),
      session = this.Session,
      reloadedWizardItem: NavPageItem; // avoid double Reload, but ensure on reload
    // Determine if a full visualization Refresh is necesary
    return WFInstance.RevertItem(true).then(() => {
      if (instanceKey != this.WFInstanceKey)
        return true; // instance has Changed  -> Full Refresh
      var wizardKey = this.WizardItem.Key,
        getKeyRef = (item: NavPageItem) => ["Key_1", "Key_2", "Key_3"].map(name => item.GetValue(name, true), this).join(","),
        keyRef = getKeyRef(this.WizardItem);
      return this.WizardItem.RevertItem(true).then((wizard) => {
        reloadedWizardItem = wizard;
        if (wizard.Key != wizardKey || keyRef != getKeyRef(wizard))
          return true; // KeyRef data on Wizard has Changed

        if (session.DisplayItem.PageName != this.WizardPagename) {
          return session.DisplayItem.RevertItem(true).then((item) => {
            // No Full Refresh but Display Data in Sync is ensured
            return false;
          });
        }
      });
    }).then((forceFullRefresh) => {
      this.WFInstanceKey = WFInstance.Key;
      let checkedOutChanged = currCheckedOutBy != WFInstance.GetValue("CheckedOutBy"),
        stepChanged = currStep != WFInstance.GetValue("CurrentWorkflowStep");

      if (checkedOutChanged)
        this.IsCheckedOut = false;

      if (checkedOutChanged || stepChanged) {
        session.destroy();
        return this.DialogService.ShowDialog({ message: `Das geöffnete Formular wurde in newsystem ${stepChanged ? 'weitergeleitet' : 'geändert'} und wurde deshalb geschlossen.` });
      } else if (forceFullRefresh) {
        return this.reloadDisplayItems(session, reloadedWizardItem && this.$c.when(reloadedWizardItem)).then(() => this.refreshActions(true)).then(() => {
          // Check if Selected Action still Valid
          return session.RefreshRoutingAction(session.ValidatedRoutingAction());
        });
      }
    });
  }

  public ReserveFunds(session: KWFFormSession) {
        if(this.IsStandalone) {
      // TODO As method gets called Elevated, this will be called in NAv Context otherwise ...
      return this.$c.reject('Für die Aktion ist eine Mittelreservierung notwendig, für einfache Workflowbenutzer kann diese Aktion deshalb nicht ausgeführt werden.')
    }
    return this.WFInstance.ExecuteMethod(WFInstance.Methods.ReserveFunds).then(m => {
      // Show Message about Reserved Fundings ...
      var result = this.$c.when(<any>undefined);
      if (m && m["Result"])
        result = result.then(() => this.DialogService.ShowDialog({ title: "Mittelreservierung", message: m["Result"] }));

      // We Must Reload the Display Item unfortunately ...
      result = result.then(() => session.DisplayItem.RevertItem(true));

      return result;
    }, err => {
      // Force Reload of Item even if error
      return session.DisplayItem.RevertItem(true).then(() => { throw err; }, xerr => { throw err; });
    });
  }

  /** Select Forwarding Comment(s) for Routing Action */
  public OnSelectRoutingAction(_action: IKWFRoutingAction): PromiseLike<IKWFRoutingInfo> {
    var currAction = _action;
    if (currAction && currAction.ActionLocked === true) {
      var errMsg = "Die Aktion '" + currAction.WFActionName + "' kann nicht gewählt werden";
      errMsg += currAction.ActionLockReason ? ": " + currAction.ActionLockReason : "!";
      throw new Error(errMsg);
    }

    var currentActionCode = (currAction && currAction.WFActionCode);
    if (!currentActionCode)
      return this.$c.when(<IKWFRoutingInfo>{ ForwardingComments: [], MandatoryActions: {} });

    var loadComments = this.loadActionComments(currentActionCode);
    var applyFieldInfos = checkPromise("Das Laden der Feldinformationen ist fehlgeschlagen", this.applyCurrentFieldInfo(<DisplayPageItem>this.Session.DisplayItem, currentActionCode));

    return loadComments.then(fwComments => {
      return applyFieldInfos.then(() => {
        var result: IKWFRoutingInfo = {
          ForwardingComments: fwComments.map(c => {
            var result = <IKWFComment>c;
            result.ID = c.WorkflowForwardingComment;
            result.Title = c.Comment;
            return result;
          }),
          MandatoryActions: currAction
        };
        return result;
      });
    });
  }

  /** Execute Method to Change Currently Displayed Item (Weiter zur Kontierung / Zurücksetzen) */
  private executeChangeItemMethod(session: KWFFormSession, nameOnItem: string, nameOnInstance?: string) {
    var me = this;

    if (!session || !session.DisplayItem || !me.WFInstance)
      return this.$c.reject("Operation nicht möglich");

    if (nameOnInstance && me.WFInstance.ClientInfo.AllMethods[nameOnInstance])
      var exec = me.WFInstance.ExecuteMethod({ MethodName: nameOnInstance });
    else
      var exec = session.DisplayItem.ExecuteMethod({ MethodName: nameOnItem });

    return exec.then(item =>
      me.reloadDisplayItems(session).then(() =>
        // Force Refresh of Mand. Props on new Display Item
        session.RefreshRoutingAction(session.ValidatedRoutingAction())));
  }

  private refreshActions(forcedRefresh?: boolean) {
    var session = this.Session,
      WFInstance = this.WFInstance.Model;
    return LoadActions(this.$c, [WFInstance.ID], this.navUrl,
      { explicitFill: forcedRefresh, groupItems: true }).then(actionGroups => {
        actionGroups = actionGroups || [];
        if (this.IsCheckedOut && (actionGroups.length != 1)) {
          if (actionGroups.length == 0)
            session.AddNotification({ message: "Es stehen keine Aktionen zur Verfügung!", messageType: "warning" });
          else
            throw new Error("Unexpected Result from loading Instance Actions [" + actionGroups.length + "]");
        }
        let kwfRouting = (actionGroups && actionGroups[0]) || { Items: [], RootItems: [], NoGroups: false };
        this.kwfActions = kwfRouting.Items;

        return checkPromise("Das Laden des aktuellen Schritts ist fehlgeschlagen",
          this.NavClient.Read<"FDRW_Workflow_Step_List">({
            PageName: "FDRW_Workflow_Step_List",
            NavUrl: this.navUrl,
            Keys: {
              Code: WFInstance.CurrentWorkflowStep,
              WorkflowPart: WFInstance.CurrentWorkflowPart
            }
          }, true)).then(wfStep => {
            this.kwfStepItem = wfStep.Data;
            session.RoutingActions = kwfRouting;
            // Initial Refresh State, React on Changes effecting state
            return session.RefreshState(this.getState());
          });
      });
  }

  private getState(): IKWFSessionState {
    const wfInstance = this.WFInstance.Model,
      $rwf = this.Session && this.Session.$rwf,
      kwfStep = this.kwfStepItem,
      IsCheckedOut = this.IsCheckedOut,
      forceReadOnly = !!wfInstance.ReadOnlyView || (!IsCheckedOut) || ($rwf && $rwf.RwfSettings.User.IsStandalone === true),
      kwfSettings = this.WFSettings;

    return <IKWFSessionState>{
      NoEditContent: forceReadOnly, // Enfored Readonly (
      NoChangeDisplay: forceReadOnly, // Enfored Readonly (
      NoRouting: !IsCheckedOut,
      SignatureEnabled: true,
      DocLink: GetKWF3DocumentUrl(wfInstance.ID, undefined, this.navUrl),
      CurrentStepName: kwfStep.Name,
            CurrentStepCode : kwfStep.Code,
      CurrentWorkflowPartName: kwfStep.WorkflowPartName,
            CurrentWorkflowPartCode : kwfStep.WorkflowPart,
      CurrentStepReadOnly: kwfStep.ViewType == rwf3.ViewType.Locked,
      AllowAddDocuments: IsCheckedOut && (!kwfSettings || kwfSettings.SupDocEnabled === true),
      LugmEnabled: kwfSettings && kwfSettings.LuGMEnabled,
    };
  }

  /** Load Workflow Instance by InstanceNo or No (WITH META - Description: we Display Dos, Logs : need captions) */
  private openWFInstance(wfInstance: NavPageItem<rwf3.IFDRW_WFInstance>, forceOpen?: boolean) {
    return OpenWFInstance(this.$c.resolve("Rwf"), wfInstance, forceOpen).then(loadInfo => {
      var item = loadInfo.Item,
        session = this.Session;
      this.PreviousCheckoutUser = loadInfo.PreviousCheckedOutBy;
      // as we might load via No, re a-ssign with ID
      // item.InstanceID : UIContext is item itself!
      session.ID = item.Anchor.InstanceID = item.GetValue("ID", true);
      var wfInstance = loadInfo.Item;
      this.IsCheckedOut = loadInfo.Opened;
      this.WFInstance = wfInstance;
      this.WFInstanceKey = wfInstance.Key;

      // Start Loading Needed Comments to avoid async gap on first action selection ...
      this.loadActionComments("");
      if (loadInfo.Error || !loadInfo.Opened) {
        let textAppendix = "";
        if (!loadInfo.Error) {
          let checkedOutBy = wfInstance.GetValue("CheckedOutBy"),
            assignedTo = wfInstance.GetValue("CurrentWorkflowUser"),
            currUser = this.WFUser,
            currUserId = currUser?.ID?.toLowerCase();
          let isSameUser = currUserId && (currUserId === (checkedOutBy && checkedOutBy.toLowerCase()) || currUserId === (assignedTo && assignedTo.toLowerCase()));

          if (checkedOutBy && !isSameUser) {
            textAppendix = " Er ist durch den Benutzer '" + RwfInvoiceDataService.ResolveUser(checkedOutBy, this.navUrl) + " (" + checkedOutBy + ")' gesperrt.";
          } else if (assignedTo && !isSameUser) {
            textAppendix = " Er ist dem Benutzer '" + RwfInvoiceDataService.ResolveUser(assignedTo, this.navUrl) + " (" + assignedTo + ")' zur Bearbeitung zugewiesen.";
          } else {
            textAppendix = " " + $('<a>', {
              text: ' Zum Bearbeiten öffnen',
              href: '',
              onClick: "KWFEditForm.OpenReadonlyFormForEdit()"
            })[0].outerHTML;
          }
        };
        session.AddNotification({ message: loadInfo.Error || ("Dieser Beleg wurde nur lesend geöffnet und kann deshalb nicht bearbeitet werden." + textAppendix), messageType: "warning" });
      }
      return this.refreshActions();
    });
  }

  /** load Relevant field Information */
  protected applyCurrentFieldInfo(displayItem: DisplayPageItem, selectedAction: string) {
    var me = this,
      session = this.Session,
      ClientInfo = displayItem && displayItem.ClientInfo,
      mainItemPageName = ClientInfo && ClientInfo.MetaData.PageName,
      /** FDRW_Workflow_Field_Used.[propname] : Name for the Property on Current Page  */
      pagePropMap: { [name in DisplayPage_Main]: keyof rwf3.IFDRW_Workflow_Field_Used } = {
        FDDI_DMS_Purchase_Document: "NameOnPageVendor",
        FDDI_DMS_Sales_Document: "NameOnPageCustomer",
        FDDI_Wizard_Create_New_DMS_Document: "NameOnPageWizard",
        FDRW_Wizard: "NameOnPageWizard", // ????
        FDRW_Anordnungsblatt: "NameOnPageErfZeileHUEL",
        FDRW_Auftragsbuchblatt: "NameOnPageVendor",
        FDRW_Auftrag: "NameOnPageCustomer"
      },
      currPageProp = pagePropMap[mainItemPageName],
      allProperties = ClientInfo && ClientInfo.AllProperties,
      huelClient = me.IsKameral && me.HuelState && me.HuelState.HuelClient,
      huelProps = huelClient && huelClient.AllProperties,
      mainLineProp = (ClientInfo && ClientInfo.MasterLineProperty) || "NoMainLine",
      // var noProp = (propName) => { throw new Error("Eigenschaft '" + propName + "' nicht vorhanden!"); return undefined; }
      noProp = fn.nullOp,
      /** Get IPageProperty Instance by Name for given 'FDRW_Workflow_Field_Used.Type' of Property */
      getProperty = <{ [propType: number]: (propName: string) => IPageProperty }>{
        // Header
        0: (propName: string) => allProperties[propName] || noProp(propName),
        //// Huel- Aufteilung
        4: (propName: string) => (huelProps && huelProps[propName]) || noProp(propName),
        // MainLine
        1: (propName: string) => allProperties[mainLineProp + "." + propName] || noProp(mainLineProp + "." + propName),
        // LuGM
        2: (propName: string) => allProperties[PROP_LuGMAddRec + "." + propName] || noProp(PROP_LuGMAddRec + "." + propName),
        // Kameral
                3: (propName: string) => allProperties[propName] || allProperties[propName.replace("Line","Subform")] || noProp(propName),
      },

      /** Resolve Property on FDRW_Workflow_Field_Used item instance */
      getProp = (item: rwf3.IFDRW_Workflow_Field_Used) => {
        var fieldName = <string>item[currPageProp];
        var getFunc = getProperty[item.Type];
        return (fieldName && getFunc && getFunc(fieldName)) || undefined;
      },

      /** Retrieve Page Properties By "Usage Type" */
      getFieldsByType = (items: rwf3.IFDRW_Workflow_Field_Used[], typeNo: rwf3.Usage) =>
        items.filter(item => (item.Usage == typeNo) && (!item.WorkflowAction || (selectedAction == item.WorkflowAction))).map(item => getProp(item)).filter(fn.isSet);

    // Changing Accounting Type/ Document Type on Wizard will change mandatory fields state
    me.trigger(ServiceEvents.ApplyFieldsInfo);

    switch (mainItemPageName) {
      case FDDI_Wizard_Create_New_DMS_Document:
        refreshFieldInfoFields = ["Document_Type", "Accounting"];
        break;
      case FDRW_Wizard:
        refreshFieldInfoFields = ["EinnahmeAusgabe"];
        break;
      default:
        var refreshFieldInfoFields = [];
    }

    if (refreshFieldInfoFields && refreshFieldInfoFields.length) {
      me.one(ServiceEvents.ApplyFieldsInfo, (<NavPageItem>displayItem).RegisterPropertyChanged(refreshFieldInfoFields, e => {
        if ((loadedFieldInfo && session && (session.DisplayItem == displayItem))) {
          applyFieldInfo(displayItem, loadedFieldInfo);
        }
      }));
    }
    const rwf3DocumentType = rwf3.Document_Type;
    var loadedFieldInfo: rwf3.IFDRW_Workflow_Field_Used[],
      stepCode = this.WFInstance.GetValue("CurrentWorkflowStep");
    return this.loadAllFieldInfo(stepCode).then(items => applyFieldInfo(displayItem, loadedFieldInfo = items));

    function getExplicitItemDocType(item: DisplayPageItem) {

      switch (item.PageName) {
        case FDRW_Auftrag:
          return rwf3DocumentType.Order;
        case FDRW_Auftragsbuchblatt:
          return rwf3DocumentType.Invoice;
        case FDRW_Anordnungsblatt:
          let Ein_Aus = item.GetValueEx("Ein_Aus");
          if (Ein_Aus == "Ausgabe")
            return rwf3DocumentType.Invoice;
          else
            return rwf3DocumentType.Credit_Memo;
        case FDRW_Wizard:
          let einAus = item.GetValueEx("EinnahmeAusgabe");
          if (einAus == "Reservierung")
            return rwf3DocumentType.Order;
          else if (einAus == "Ausgabe_SK" || einAus == "Ausgabe_PK" || einAus == "Auftrag")
            return rwf3DocumentType.Invoice;
          else
            return rwf3DocumentType.Credit_Memo;

        case FDDI_Wizard_Create_New_DMS_Document:
          let creationType = item.GetValueEx("Creation_Type");
          if (creationType == "Reservation")
            return rwf3DocumentType.Order;
          else
            // +1: Enum on FieldsInfo has Default '0', on Items not!
            return <rwf3.Document_Type>(item.GetValue(PROP_Document_Type) + 1);

        case FDDI_DMS_Purchase_Document:
          return <rwf3.Document_Type>(item.GetValue(PROP_Document_Type) + 1);
        case FDDI_Wizard_Create_New_DMS_Document:
          return <rwf3.Document_Type>(item.GetValue(PROP_Document_Type) + 1);
        case FDDI_DMS_Sales_Document:
          return <rwf3.Document_Type>(item.GetValue(PROP_Document_Type) + 1);
        default:
          fn.never(item, "Unxpected page " + (item as DisplayPageItem).PageName);
        // ??
      }
    }

    function applyFieldInfo(item: DisplayPageItem, allData: rwf3.IFDRW_Workflow_Field_Used[]) {
      if (!item) {
        return;
      }
      var explDocType = getExplicitItemDocType(item),
        validDocTypes = [rwf3DocumentType._blank_, explDocType || rwf3DocumentType._blank_];
      var validAccountings = getCurrentValidAccounting(getCurrentAccountingType(item)),
        validData = allData.filter(item => validAccountings.indexOf(item.Accounting) >= 0)
          .filter(item => validDocTypes.indexOf(item.Document_Type) >= 0),
        mainData = validData.filter(item => item.Type !== rwf3.Type_.HUELAufteilung), // HUEL Aufteilung is seperate
        huelData = validData.length != mainData.length && validData.filter(item => item.Type == rwf3.Type_.HUELAufteilung);

      var mainInfo = resolveInfo(ClientInfo, mainData),
        huelInfo = huelData && huelData.length && huelClient && resolveInfo(huelClient, huelData);

      if (me.trigger(ServiceEvents.ApplyMandatoryFields, { FieldInfo: mainInfo, HuelInfo: huelInfo })) {
        // trigger == true : event got cancelled : reset Mandatory fields
        mainInfo.Mandatory.length = 0;
        if (huelInfo) {
          huelInfo.Mandatory.length = 0;
        }
      }

      me.CurrentWizardOnlyFields = (mainInfo.WizardOnly || []).map(item => item.Path);
      item.Anchor.Mandatory.ReplaceValues(KWF_FieldMarker, mainInfo.Mandatory);
      item.Anchor.Readonly.ReplaceValues(KWF_FieldMarker, mainInfo.Locked);
      // Store explict Unlocked Fields for later Evaluation
      item.Anchor.Readonly.ExplicitUnlocked = mainInfo.ExplicitUnlocked;

      function getCurrentAccountingType(item: DisplayPageItem): accountingType {
        if (!item) {
          return;
        }
        switch (item.PageName) {
          case FDDI_DMS_Purchase_Document:
            return "Vendor";
          case FDDI_DMS_Sales_Document:
            return "Customer";
          case FDRW_Anordnungsblatt:
          case FDRW_Auftragsbuchblatt:
            return "Vendor"; // ???
          case FDDI_Wizard_Create_New_DMS_Document:
            var acc = <accountingType>item.GetValueEx("Accounting");
            if (acc == "Vendor" || acc == "Customer")
              return acc;
            return "";
          case FDRW_Wizard:
            var erfassungsTyp = item.GetValueEx("EinnahmeAusgabe");
            if (erfassungsTyp != "Reservierung")
              return "Vendor";
            return "";
          case FDRW_Auftrag:
            return "";
          default:
            fn.never(item, (item as NavPageItem).PageName);
        }
      }
      type accountingType = "" | "_blank" | "Vendor" | "Customer";
      function getCurrentValidAccounting(t: accountingType) {
        var acc = rwf3.Accounting_;
        switch (t) {
          case "Vendor":
            return [acc._blank_, acc.Vendor];
          case "Customer":
            return [acc._blank_, acc.Customer];
          default:
            return [acc._blank_];
        }
      }

      function resolveInfo(this: void, client: INavPageClientInfo, data: rwf3.IFDRW_Workflow_Field_Used[]) {
        // always calculate unlocked (for use in special Handling  [Order]...)
        var _explicitUnlocked = getFieldsByType(data, rwf3.Usage.Unlocked);
        // Calculate effective Locked Properties
        if (me.kwfStepItem.ViewType == rwf3.ViewType.Locked) {
          // Locked View : all Except some!
          var unlockedProperties = _explicitUnlocked;
        } else {
          // Regular Locked Properties
          var lockedProperties = getFieldsByType(data, rwf3.Usage.Locked);
        }

        /** Mandatory Properties (Locked and mandatory makes no sense!) */
        const mandatoryProperties = getFieldsByType(data, rwf3.Usage.Mandatory),
          wizardOnlyFieldUsed = (me.WizardPagename == client.PageName) &&
            mainData.filter(item => item.Usage == rwf3.Usage.Mandatory && ["NameOnPageVendor", "NameOnPageCustomer"].every(pg => !item[pg])),
          wizardOnlyMandProps = getFieldsByType(wizardOnlyFieldUsed || [], rwf3.Usage.Mandatory);

        //#region Add Custom "Header2MainLine synced Mandatory/Locked/Unlocked Properties"
        function addCustomDepProp(name: string, depName: string, depType: number, excludeMandatory?: boolean) {
          // excludeMandatory : Some Scenarios : Disabled only as a "Search Helper fields"
          [excludeMandatory ? undefined : mandatoryProperties, lockedProperties, unlockedProperties, wizardOnlyMandProps].filter(item => item && item.length > 0).forEach(container => {
            var matchMainProp = container.filter(item => item.Name == name)[0];
            var matchDepProp = matchMainProp && getProperty[depType](depName);
            if (matchDepProp)
              container.push(matchDepProp);
          });
        }
        function addCustomLineProp(name: string, lineName: string, excludeMandatory?: boolean) {
          addCustomDepProp(name, lineName, 1, excludeMandatory);
        }
        let pageName = <DisplayPage_Main>client.MetaData.PageName;
        switch (pageName) {
          case FDDI_DMS_Purchase_Document:
          case FDDI_DMS_Sales_Document:
            addCustomLineProp("Due_Date", "DDate");
            addCustomLineProp("Posting_Date", "PDate");
                        addCustomLineProp("MwSt_Datum","LDate"),
              addCustomLineProp("Gemeindenr", "CommNo");
            addCustomLineProp("Mahnart", "Mahnart");
            //, "Sachkonto_Buch_Gruppe",  "Mahnart", "Mahnart" --> not necessary/useful
            break;
          case FDDI_Wizard_Create_New_DMS_Document:
            addCustomDepProp("No", "IBAN", 0, true);
            addCustomDepProp("No", "BankkontoCode", 0, true);
            break;
          case FDRW_Wizard:
            addCustomDepProp("Adressnr", "IBAN", 0, true);
            break;
          default:
          // ??
        }

        //#endregion

        if (unlockedProperties) {
          // means:  reverse Logic : all Locked except those
          let unlockedParents = fn.distinct(unlockedProperties.slice().map(item => item.ParentProp).filter(fn.isSet));
          // Parents of Unlocked Props mut be unlocked too
          unlockedProperties.push(...unlockedParents);

          var lockedProperties = fn.except(fn.DictToArray(allProperties), unlockedProperties);
        }

        return {
          ExplicitUnlocked: _explicitUnlocked,
          Locked: lockedProperties,
          Mandatory: mandatoryProperties,
          WizardOnly: wizardOnlyMandProps || []
        };
      }
    }
  }

  /** Load FieldInfo Relevant for this Instance */
  private loadAllFieldInfo(stepCode: string) {
    var result = this._field_used_PerStep[stepCode];
    if (!result) {
      result = this._field_used_PerStep[stepCode] = this.NavClient.ReadMultiple<rwf3.IFDRW_Workflow_Field_Used>({
        PageName: "FDRW_Workflow_Field_Used",
        PageCount: 0, // readAll
        Constraints: [
          { FieldName: "WorkflowStep", Value: "'" + stepCode + "'" },
        ]
      }, this.navUrl, true).then(d => <rwf3.IFDRW_Workflow_Field_Used[]>d.Data);
    }
    return result;
  }
  private _field_used_PerStep: { [stepCode: string]: PromiseLike<rwf3.IFDRW_Workflow_Field_Used[]> } = {};

  /** Retrieve Comments for given Action */
  private loadActionComments(currentActionCode: string) {
    if (!this["_loadAllCMtsInfo"]) {
      var result = this["_loadAllCMtsInfo"] = this.NavClient.ReadMultiple<"FDRW_WF_Action_Fwd_Comments_List">({
        PageName: "FDRW_WF_Action_Fwd_Comments_List",
        Constraints: [{ FieldName: "WorkflowStep", Value: this.WFInstance.GetValue("CurrentWorkflowStep", true) }],
        PageCount: 0 // readAll
      }, this.navUrl, true).then(d => {
        // Implicit Invalidate Comments on Current Workflow Step Changed
        var once = this.WFInstance.RegisterPropertyChanged("CurrentWorkflowStep", () => {
          delete this["_loadAllCMtsInfo"];
          once();
        });
        this.Session.one(_Events.destroyed, once);
        return <rwf3.IFDRW_WF_Action_Fwd_Comments_List[]>d.Data;
      });
    } else {
      result = this["_loadAllCMtsInfo"];
    }

    return checkPromise("Das Laden der Weiterleitungskommentare ist fehlgeschlagen",
      result.then(item => item.filter(d => {
        return currentActionCode && d.WorkflowAction == currentActionCode;
      })));
  }

  /** Init Model for "Breakout" Functionality if applicaple */
  private initBreakout(session: KWFFormSession) {
    var me = this,
      instance = me.WFInstance,
      NavClient = me.NavClient,
      dlg = this.DialogService,
      getVal = instance && fn.proxy(instance.GetValue, instance),
      currBreakOutId: string,
      selectedBreakoutTemplate: rwf3.IFDRW_Workflow_Breakouts,
      breakOuts = AsObservableArray(<rwf3.IFDRW_Workflow_Step_Breakouts[]>[]),
      isOnWizard = () => session && (session.DisplayItem?.PageName == session.WizardPagename),
      isFWF = () => !!session?.KWFService?.SourceRecordInfos,
      show = () => isOnWizard() || isFWF();

    var lastLoad: PromiseLike<any>;
    function loadDisplayBreakouts() {
      selectedBreakoutTemplate = undefined;
      if (currBreakOutId) { // Show the Selected Only
        return lastLoad = NavClient.ReadMultiple<rwf3.IFDRW_Workflow_Breakouts>({
          PageName: rwf3Pages.FDRW_Workflow_Breakouts.PageName,
          Constraints: [{ FieldName: "Code", Value: currBreakOutId }],
          PageCount: 0,
        }, me.navUrl).then(d => {
          selectedBreakoutTemplate = d.Data[0];
          var item = <rwf3.IFDRW_Workflow_Breakouts>selectedBreakoutTemplate || { Code: currBreakOutId, Description: "<" + currBreakOutId + ">" };
          var t = <any>kendo.observable(<rwf3.IFDRW_Workflow_Step_Breakouts>{ BreakoutCode: item.Code, BreakoutDescription: item.Description });
          result.Selected = t;
          breakOuts.splice(0, breakOuts.length, t);
          return breakOuts;
        });
      } else
        if (getVal("BreakoutsAvailable")) { // Show Available breakouts
          return lastLoad = NavClient.ReadMultiple<rwf3.IFDRW_Workflow_Step_Breakouts>({
            PageName: rwf3Pages.FDRW_Workflow_Step_Breakouts.PageName,
            Constraints: [{ FieldName: "WFStepID", Value: getVal("CurrentWorkflowStep") }],
            PageCount: 0
          }, me.navUrl, true).then(d => {
            breakOuts.splice(0, breakOuts.length, ...d.Data);
            result.Selected = breakOuts.length == 0 && breakOuts[0] || undefined;
            return breakOuts;
          });
        } else {
          breakOuts.splice(0, breakOuts.length);
          return lastLoad = me.$c.when(null);
        }
    }

    function breakOutIdChanged() {
      currBreakOutId = getVal("CurrentBreakout");
      if (result)
        result.Select();

      const afterRefresh = () => {
        if (result) {
          // PreSelect Single Item on UI
          if (!result.Selected && !me.Session.NoEdit && breakOuts && breakOuts.length == 1) {
            result.Selected = breakOuts[0];
          }
          result.Select();
        }
      };
      const afterLoad = () => me.Session.RefreshState().then(afterRefresh, afterRefresh);
      return loadDisplayBreakouts().then(afterLoad, afterLoad);
    }

    function resolveBreakOut(id: string) {
      return (id && breakOuts && breakOuts.filter(b => b.BreakoutCode == id)[0]) || undefined;
    }

    /** We reset Routing action no Create/Delete as we INterfer on Select Routing action and evt. reset Mandatory Actions */
    function resetRouting() {
      return session.RefreshRoutingAction(null).then(() => {
        if (session.ValidatedRoutingAction())
          throw new Error("Aktion zurücksetzen fehlgeschlagen!");
      });
    }

    var teardownOpenNavRefresh = fn.noop;
    var result = {
      /** Should be visible on UI? */
      Show: (breakOuts && breakOuts.length > 0) || !!currBreakOutId,
      /** Available Breakout Items for User Selection */
      Items: breakOuts,
      /** Selected Item on UI */
      Selected: resolveBreakOut(currBreakOutId),
      /* Create Selected Breakout Action */
      Create: {
        Enabled: false,
        Hidden: false,
        Execute: () => session.EnqueueOperation(() => {
          return resetRouting().then(() => {
            var selBreakout = result.Selected,
              lookupPage = result.Selected.BreakoutServiceName,
              recId: string;
            // For some Pages we must "override" the custom filter logic ...
            const constraints: INamed<IPageQueryConstraint[]> = {
              "FDDI_Vertragsübersicht": [
                { FieldName: "Beschreibung", Value: "*|«-1»" }, // Aufheben der FilterEinschränkung 'Bezug zu aktueller LuGM Zeile''
                { FieldName: "Vertragsklasse", Value: "Vertrag" }],
              "FDRW_EVU_Contract_List": [],
              "FDRW_Reinigungsvertragsuebersicht": []
            };

            const DisplayItem = session.DisplayItem || session.KWFService.WizardItem;
            // For some Pages we must "override" the custom initial Display Fields ...
            const searchFields: INamed<(string | IPageQueryConstraint)[]> = {
              "FDDI_Vertragsübersicht": ["Beschreibung", "Name",
                { FieldName: "Kreditorennr", Value: DisplayItem.GetValue("No", false) || "" }],
              "FDRW_EVU_Contract_List": [
                "Description", "Name_dop",
                { FieldName: "Vendor_No_dop", Value: DisplayItem.GetValue("No", false) || "" }
              ],
              "FDRW_Reinigungsvertragsuebersicht": [
                "Beschreibung", "Name",
                { FieldName: "Kreditorennr", Value: DisplayItem.GetValue("No", false) || "" }]
            };

            if (lookupPage) {
              var uiSelect = ShowModalReadMultipleSelect({
                TargetPage: lookupPage,
                Title: `Auswahl '${selBreakout.BreakoutDescription}'`,
                Constraints: constraints[lookupPage],
                InitialSearchFields: searchFields[lookupPage],
                PersistToken: "Breakout_" + lookupPage
              }, this.$c).then(selItem => {
                if (!selItem)
                  fn.error.ThrowCancel();
                return NavClient.GetRecIdFromKey(this.navUrl, lookupPage, selItem["Key"]).then(selRecId => {
                  recId = selRecId;
                });
              });
            }

            return NavClient.$c.when(uiSelect).then(() => {
              return instance.ExecuteMethod(
                WFInstance.Methods.StartBreakout(instance.Key, selBreakout.BreakoutCode, recId || ""));
            });
          });
        })
      },
      /** Cancel Currently Active Breakout Action */
      Cancel: {
        Enabled: false,
        Hidden: false,
        Execute: () => session.EnqueueOperation(() => dlg.WhenConfirm("Fachprozess '" + (resolveBreakOut(currBreakOutId) || { BreakoutDescription: currBreakOutId }).BreakoutDescription + "' abbrechen?").then(
          () => resetRouting().then(fn.proxy(instance.ExecuteMethod, instance, WFInstance.Methods.CancelBreakout))
        ))
      },
      /** Open Nav Client Action */
      OpenNav: {
        Enabled: false,
        Execute: () => OpenBreakoutInNav(this.$c, instance).then(wnd => {
          if (!wnd) return;

          teardownOpenNavRefresh();
          var refreshPending = false,
            timer = window.setInterval(() => {
            try {
              if (wnd.closed) {
                window.clearInterval(timer);
                timer = undefined;
                refresh();
              }
            } catch (e) {
              window.clearInterval(timer);
              timer = undefined;
            }
          }, 500);

          function refresh() {
            if (refreshPending || session.IsDestroyed)
              return;

            refreshPending = true;
            session.EnqueueOperation(() => me.RevertDisplay()).then(() => {
              refreshPending = false;
              if (session.IsDestroyed || !getVal("CurrentBreakout"))
                teardownOpenNavRefresh();
            }, () => refreshPending = false);
          }

          teardownOpenNavRefresh = () => {
            if (timer)
              window.clearInterval(timer);
            timer = undefined;
            window.removeEventListener("focus", refresh);
            teardownOpenNavRefresh = fn.noop;
          };
          window.addEventListener("focus", refresh);
          session.one(_Events.destroyed, teardownOpenNavRefresh);
        })
      },
      /* Select 'Action': react on DropDown Change ('e' is set, or explicitly refresh State (e not set) */
      Select: (e?: kendo.ui.DropDownListEvent) => {
        if (currBreakOutId !== (currBreakOutId = getVal("CurrentBreakout"))) {
          // Changed, but not from UI, toDO : Refresh Selected?
        }
        var selected = result.Selected;
        result.Show = (show()) && (!!currBreakOutId || breakOuts.length > 0);
        result.Create.Enabled = me.IsCheckedOut && !!(selected && selected.BreakoutCode && !currBreakOutId);
        result.OpenNav.Enabled = !!currBreakOutId;
        result.Cancel.Enabled = me.IsCheckedOut && !!currBreakOutId;
        result.Select["Enabled"] = me.IsCheckedOut && !currBreakOutId; // Disabled DropDown on UI
        result.Cancel.Hidden = !result.Cancel.Enabled;
        result.Create.Hidden = !result.Cancel.Hidden;

        // if called from DrowDwn Change: kendo trigger event not automaticaly requests ng- dicgest cylce
        const element = e && e.sender && e.sender.element;
        if (element) {
          this.$c.tick(element);
        }
      }
    };

    if (instance && instance.ClientInfo.AllProperties["CurrentBreakout"]) {

      session.RegisterDisplayChange(() => result.Select());

      // With Active Breakout Mandatory Properties should be ignored :
      // Reset Mandatory Actions on Select Routing Action
      me.bind(ServiceEvents.ApplyMandatoryFields, (e: IEvent) => {
        if (getVal("ReadOnlyView") == true && !!getVal("CurrentBreakout")) {
          e.preventDefault();
        }
      });

      var isCompletedBreakout = function isCompletedAndNotEditableBreakout() {
        if (selectedBreakoutTemplate &&
          selectedBreakoutTemplate.EditableAfterCompletion === false) {
          return true;
        }
      };

      var isOpenBreakoutOnWizard = function isOpenBreakoutOnWizard() {
        if (show() && currBreakOutId) {
          return true;
        }
      };

      // NO Edit if BreakoutTemplate forbides Editing
      var f2 = session.bindWithDefer((e) => {
        // TODO : Reset State .... WTF!!!
        var state = session.CustomState;
        if (isCompletedBreakout()) {
          state.NoEditContent = true;
          state.NoChangeDisplay = false;
          state.NoRouting = currBreakOutId && selectedBreakoutTemplate?.ForwardingForbidden == true;
        } else if (isOpenBreakoutOnWizard()) {
          state.NoEditContent = true;
          state.NoChangeDisplay = true;
          state.NoRouting = selectedBreakoutTemplate?.ForwardingForbidden == true
        } else {
          state.NoEditContent = false;
          state.NoChangeDisplay = false;
          state.NoRouting = false;
        }
      }, _Events.RefreshState);

      // State Change Refresh necessary when instance Changes
      session.one(_Events.destroyed, instance.RegisterPropertyChanged("CurrentBreakout", breakOutIdChanged));
      return breakOutIdChanged().then(() => {
        if (currBreakOutId) {
          (lastLoad || loadDisplayBreakouts()).then((b) => {
            // Dialog?
            if (!isFWF() && !getVal("ReadOnlyView") && isCompletedBreakout()) {
              var title = (breakOuts[0] && breakOuts[0].BreakoutDescription) || "???",
                msg = "Geschützte Ansicht:\n Die Kontierung wurde aus einem '" + title + "' generiert und kann im Rechnungsworkflow nicht mehr geändert werden. \n" +
                  "Für etwaige Korrekturen verwenden Sie bitte die Funktion 'Zurücksetzen'.";

              session.AddNotification({ message: msg, messageType: "warning" });
            }
            return session.RefreshState();
          });
        }
      }).then(fn.ToFunc(result));
    }
    return me.$c.when(undefined);
  }
}

/** Description of Page "FDRW_WFInstance" */
const WFInstance = rwf3Pages.FDRW_WFInstance;
/** Custom Mandatory/Readonly Field Marker Token */
const KWF_FieldMarker = "KWFDefault";

/** Validate HuelAufteilung : Show Dialog and Cancel if an invalid one is found */
function validateHuelAufteilung(this: void, service: RWF3BaseService) {
  const session = service.Session;
  var $c = session.KWFService.NavClient.$c,
    dlg = $c.resolve("DialogService"),
    action = session.ValidatedRoutingAction();

  var needValidateHuelAufteilung = action && action.CheckHUELAufteilung && service.IsKameral;

  return $c.when(needValidateHuelAufteilung).then((needValidateHuelAufteilung) => {
    if (!needValidateHuelAufteilung)
      return $c.when();

    const currItem = session.DisplayItem,
      MethodCheckHuelAufteilung = "CheckHuelAufteilung",
      DisplayPageName = session.DisplayPageName,
      source = currItem.Anchor.Source,
      items = fn.ToArray(source.Container?.Items || [currItem]);

    if (DisplayPageName == FDRW_Wizard)
      return $c.reject("Für diese Aktion ist die Gültigkeit der Hülaufteilung notwendig, ein Weiterleiten aus der Vorkontierungsmaske ist deshalb nicht möglich");

    if (service.IsStandalone || DisplayPageName == FDRW_Auftrag || !source.ClientInfo.AllMethods[MethodCheckHuelAufteilung]) {
      return $c.when();
    }

    return session.EnsureCommit().then(() => {
      //fn.sort(items, item => (item == currItem) ? 0 : 1); // Check Current as First
      return items.reduce((prev, curr) => {
        return prev.then(() => {
          return curr.ExecuteMethod({ MethodName: MethodCheckHuelAufteilung }, undefined, true).then(fn.noop, err => {
            // Position on Item
            if (source.Container && currItem.ClientKey != curr.ClientKey)
              source.Container.SetSelected(currItem);
            dlg.ShowDialog({ title: "Fehler bei der Prüfung der HÜL Aufteilung", message: ErrToMsg(err) });
            fn.error.ThrowCancel(err);
          });
          // Configured Validation
          //return this.GetHuelAufteilung(curr, true).then(huelItems => {
          //    var invalidItem: nav.data.NavPropertyValue;
          //    if (huelItems && (invalidItem = huelItems.Anchor.FindFirstInvalid())) {
          //        dlg.ShowError("TODO: HUEL-Wert '" + invalidItem.Property.Title + "' muss gefüllt werden!");
          //        fn.error.ThrowCancel();
          //    }
          //});
        });
      }, $c.when<any>(undefined));
    });
  });
}

/** Form Field Info for Locked/ Mandatory Properties */
export interface IFormFieldInfo {
  Locked: IPageProperty[];
  Mandatory: IPageProperty[];
}

export interface IKamHuelState {
  Show: boolean;
  Title: string;
  HuelClient: INavPageClientInfo;
  HuelFields: IFormFieldInfo;
  Current: NavPageItems;
}

export interface IKamAdressInfo {
  /** Current Adress Info Page Item */
  Item: NavPageItem<IFDRW_AdresseKarte>;
  /** Current AddressNo */
  AddressNo: string;
  /** Current State of AdressInfo */
  Loading: boolean;
  /** Error Info on failure */
  Error: string;
}

/** Standalone only page info */
interface IFDRW_Workflow_Budget_Info extends IPageObj {
  DocumentNo: string;
  LineNo: string;
  Info: string;
}
